"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";

/**
 * IdleHint — subtle bottom-centre line at boot showing the presenter how
 * to reveal the chrome. Auto-dismisses 12 s after the first SSE event,
 * the first hotkey, or the first scenario start. Stays out of the audience
 * frame: low contrast, low z-index, easy to miss.
 *
 * Pure presenter ergonomic — never visible during a real demo because the
 * presenter has revealed the chrome and started a scenario by then.
 */
export function IdleHint({ active, className }: { active: boolean; className?: string }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(false), 12_000);
    return () => clearTimeout(t);
  }, [active]);

  if (!visible || !active) return null;

  return (
    <div
      aria-hidden="true"
      data-component="idle-hint"
      className={clsx(
        "pointer-events-none fixed bottom-3 left-1/2 z-10 -translate-x-1/2 select-none",
        "rounded-full border border-state-verified/20 bg-orbitra-ink/60 px-4 py-1.5 backdrop-blur-sm",
        "font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft/70",
        "transition-opacity duration-state ease-state",
        className,
      )}
    >
      <kbd className="rounded border border-state-verified/30 bg-state-verified/10 px-1.5 py-0.5 text-state-verified">?</kbd>
      <span className="mx-2">or</span>
      <kbd className="rounded border border-state-verified/30 bg-state-verified/10 px-1.5 py-0.5 text-state-verified">⌘ ,</kbd>
      <span className="ms-2">to reveal presenter chrome</span>
    </div>
  );
}
